import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { StateBadge } from '@/components/platform/StateBadge';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import { ADMIN_ROLE_CODES, INVITATION_TYPES } from '@/types/platform';
import type {
  AdministratorInvitation,
  InstitutionAdministratorsResponse,
  InvitationType,
} from '@/types/platform';
import { formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-07: invite the people who will run this institution, and see what happened
 * to the invitation afterwards.
 *
 * Status is the whole point. An invitation that was sent and an invitation that
 * bounced look identical from CSKY's side unless the screen says so, and the
 * failure mode that hurts is an institution sitting in
 * ADMIN_INVITATION_PENDING for a week because one address was mistyped. So
 * every invitation carries its status, when it was last sent, when it expires,
 * and the delivery error if there was one — and FAILED and EXPIRED invitations
 * are pulled to the top rather than sorted by date with everything else.
 *
 * The invitation carries contract role names (SCHOOL_ADMIN, PRINCIPAL, …) and
 * an invitation type, because that is the contract the identity service
 * validates; what those resolve to as catalogue roles is the service's
 * decision. STANDARD invitations are deliberately excluded from the server's
 * listing, so only PRIMARY and SECONDARY are offered here — an invitation this
 * console cannot show afterwards is not a type worth offering.
 */

function invitationTypeLabel(type: InvitationType, t: ReturnType<typeof useTranslation>['t']): string {
  return t(`platform.admins.invitationType.${type}`, { defaultValue: type });
}

function roleLabel(name: string, t: ReturnType<typeof useTranslation>['t']): string {
  return t(`platform.admins.role.${name}`, { defaultValue: name });
}

/** Invitations that need someone to act, before those that do not. */
function byUrgency(a: AdministratorInvitation, b: AdministratorInvitation): number {
  const rank = (invitation: AdministratorInvitation) =>
    invitation.status === 'FAILED' ? 0 : invitation.status === 'EXPIRED' ? 1 : invitation.status === 'PENDING' ? 2 : 3;
  return rank(a) - rank(b);
}

export function InstitutionAdministratorsPanel({
  institutionId,
  institutionName,
}: {
  institutionId: string;
  institutionName: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const fetcher = useCallback(() => platformApi.administrators.list(institutionId), [institutionId]);
  const { state, reload, replace } = useAsyncData<InstitutionAdministratorsResponse>(
    `institution-admins:${institutionId}`,
    fetcher,
    institutionId.length > 0,
  );

  const [inviteOpen, setInviteOpen] = useState(false);
  const [revoking, setRevoking] = useState<AdministratorInvitation | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const applyInvitation = (updated: AdministratorInvitation) => {
    if (state.status !== 'ready') return;
    const existing = state.data.invitations ?? [];
    replace({
      ...state.data,
      invitations: existing.some((entry) => entry.id === updated.id)
        ? existing.map((entry) => (entry.id === updated.id ? updated : entry))
        : [...existing, updated],
    });
  };

  const resend = async (invitation: AdministratorInvitation) => {
    setBusyId(invitation.id);
    setRowError(null);
    try {
      applyInvitation(await platformApi.administrators.resend(invitation.id));
    } catch (cause) {
      setRowError((cause as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Revocation goes through the confirmation dialog like every other destructive
   * action.
   *
   * It used to fire straight from the row with a hardcoded English reason. Two
   * things were wrong with that. The reason is written into the audit trail and
   * read months later by someone reconstructing what happened, and
   * "Revoked by platform administrator" tells them only what they could already
   * see from the actor column. And it was one click: revoking invalidates the
   * link the invited administrator is holding, which is not undoable — a resend
   * issues a NEW token — so an institution can end up stuck in
   * ADMIN_INVITATION_PENDING because of a misclick on a crowded row.
   */
  const revoke = async (invitation: AdministratorInvitation, reason: string) => {
    setBusyId(invitation.id);
    setRowError(null);
    try {
      applyInvitation(await platformApi.administrators.revoke(invitation.id, reason));
      setRevoking(null);
    } catch (cause) {
      setRowError((cause as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Panel
        title={t('platform.admins.title')}
        icon="users"
        actions={
          <button type="button" className={styles.primaryBtnSm} onClick={() => setInviteOpen(true)}>
            <Icon name="mail" size={15} />
            {t('platform.admins.invite')}
          </button>
        }
      >
        <AsyncPanel
          state={state}
          onRetry={reload}
          isEmpty={(data) => (data.invitations?.length ?? 0) === 0}
          emptyTitle={t('platform.admins.emptyTitle')}
          emptyBody={t('platform.admins.emptyBody')}
        >
          {(data) => {
            const invitations = data.invitations ?? [];
            const pending = invitations.filter((invitation) => invitation.status !== 'ACCEPTED');
            const active = invitations.filter((invitation) => invitation.status === 'ACCEPTED');

            return (
              <>
                {pending.length > 0 && (
                  <>
                    <h3 className={styles.subheading}>{t('platform.admins.invitations')}</h3>
                    <ul className={styles.rowList}>
                      {[...pending].sort(byUrgency).map((invitation) => (
                        <li key={invitation.id} className={styles.row}>
                          <div className={styles.rowMain}>
                            <p className={styles.rowTitle}>{invitation.email}</p>
                            <p className={styles.rowMetaLine}>
                              {invitationTypeLabel(invitation.invitationType, t)}
                              {invitation.initialRoleNames.length > 0
                                ? ` · ${invitation.initialRoleNames.map((name) => roleLabel(name, t)).join(', ')}`
                                : ''}
                              {invitation.lastSentAt
                                ? ` · ${t('platform.admins.sent', { when: formatRelative(invitation.lastSentAt, locale) })}`
                                : ''}
                              {invitation.expiresAt
                                ? ` · ${t('platform.admins.expires', { when: formatDateTime(invitation.expiresAt, locale) })}`
                                : ''}
                              {invitation.resendCount
                                ? ` · ${t('platform.admins.resent', { n: invitation.resendCount })}`
                                : ''}
                            </p>
                          </div>
                          <div className={styles.rowAside}>
                            <StateBadge kind="invitation" value={invitation.status} size="sm" />
                            <button
                              type="button"
                              className={styles.linkBtn}
                              disabled={busyId === invitation.id}
                              onClick={() => resend(invitation)}
                            >
                              {t('platform.admins.resend')}
                            </button>
                            {(invitation.status === 'PENDING' || invitation.status === 'FAILED') && (
                              <button
                                type="button"
                                className={styles.linkBtnDanger}
                                disabled={busyId === invitation.id}
                                onClick={() => setRevoking(invitation)}
                              >
                                {t('platform.admins.revoke')}
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {active.length > 0 && (
                  <>
                    <h3 className={styles.subheading}>{t('platform.admins.active')}</h3>
                    <ul className={styles.rowList}>
                      {active.map((invitation) => (
                        <li key={invitation.id} className={styles.row}>
                          <div className={styles.rowMain}>
                            <p className={styles.rowTitle}>{invitation.email}</p>
                            <p className={styles.rowMetaLine}>
                              {invitationTypeLabel(invitation.invitationType, t)}
                              {invitation.initialRoleNames.length > 0
                                ? ` · ${invitation.initialRoleNames.map((name) => roleLabel(name, t)).join(', ')}`
                                : ''}
                              {invitation.acceptedAt
                                ? ` · ${t('platform.admins.accepted', { when: formatRelative(invitation.acceptedAt, locale) })}`
                                : ''}
                            </p>
                          </div>
                          <div className={styles.rowAside}>
                            <StateBadge kind="invitation" value={invitation.status} size="sm" />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {rowError && (
                  <p className={styles.formError} role="alert">
                    {rowError}
                  </p>
                )}
              </>
            );
          }}
        </AsyncPanel>
      </Panel>

      {revoking && (
        <ConfirmActionDialog
          open
          title={t('platform.admins.revokeTitle')}
          intro={t('platform.admins.revokeIntro', { email: revoking.email })}
          tone="warning"
          impactStatus="ready"
          impact={
            <ul className={styles.impactList}>
              <li>{t('platform.admins.revokeImpactLink')}</li>
              <li>{t('platform.admins.revokeImpactStalled')}</li>
              <li>{t('platform.admins.revokeImpactAudit')}</li>
            </ul>
          }
          confirmLabel={t('platform.admins.revoke')}
          submitting={busyId === revoking.id}
          error={rowError}
          onCancel={() => setRevoking(null)}
          onConfirm={(reason) => revoke(revoking, reason)}
        />
      )}

      <InviteDialog
        open={inviteOpen}
        institutionId={institutionId}
        institutionName={institutionName}
        onClose={() => setInviteOpen(false)}
        onInvited={(invitation) => {
          applyInvitation(invitation);
          setInviteOpen(false);
        }}
      />
    </>
  );
}

function InviteDialog({
  open,
  institutionId,
  institutionName,
  onClose,
  onInvited,
}: {
  open: boolean;
  institutionId: string;
  institutionName: string;
  onClose: () => void;
  onInvited: (invitation: AdministratorInvitation) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [invitationType, setInvitationType] = useState<InvitationType>(INVITATION_TYPES[0]);
  const [roleName, setRoleName] = useState<string>(ADMIN_ROLE_CODES[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const valid = emailValid && firstName.trim().length > 0 && lastName.trim().length > 0;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const invitation = await platformApi.administrators.invite(institutionId, {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        invitationType,
        initialRoleNames: [roleName],
      });
      setEmail('');
      setFirstName('');
      setLastName('');
      onInvited(invitation);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.admins.inviteTitle')}
      description={t('platform.admins.inviteLead', { name: institutionName })}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.admins.fieldEmail')}</span>
          <input
            className={styles.input}
            type="email"
            value={email}
            required
            aria-invalid={email.length > 0 && !emailValid}
            aria-describedby="invite-email-hint"
            onChange={(event) => setEmail(event.target.value)}
          />
          <span className={styles.fieldHint} id="invite-email-hint">
            {t('platform.admins.fieldEmailHint')}
          </span>
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.admins.fieldFirstName')}</span>
            <input className={styles.input} value={firstName} required onChange={(event) => setFirstName(event.target.value)} />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.admins.fieldLastName')}</span>
            <input className={styles.input} value={lastName} required onChange={(event) => setLastName(event.target.value)} />
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.admins.fieldPhone')}</span>
          <input className={styles.input} type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.admins.fieldType')}</span>
          <Select
            value={invitationType}
            onChange={(value) => setInvitationType(value as InvitationType)}
            options={INVITATION_TYPES.filter((type) => type !== 'STANDARD').map((type) => ({
              value: type,
              label: invitationTypeLabel(type, t),
            }))}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.admins.fieldRole')}</span>
          <Select
            value={roleName}
            onChange={setRoleName}
            options={ADMIN_ROLE_CODES.map((code) => ({ value: code, label: roleLabel(code, t) }))}
          />
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.admins.inviting') : t('platform.admins.invite')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
